import { supabase } from './supabase/supabase';

class TimeSyncService {
  constructor() {
    this.offset = 0;
    this.isSynced = false;
    this.syncPromise = null;
  }

  /**
   * Initiates synchronization with the server time.
   * Uses a simple RPC call 'get_server_time' if available, 
   * or estimates based on response headers if possible (though Supabase client abstracts this).
   * For now, we will assume an RPC function 'get_server_time' exists that returns { time: 'ISO...' }
   * Fallback strategy: If RPC fails, we might use a simple table query overhead roughly, 
   * or default to 0 with a warning.
   */
  async sync() {
    if (this.syncPromise) return this.syncPromise;

    this.syncPromise = (async () => {
      const start = Date.now();
      try {
        // Try to call a dedicated RPC function for time
        // You should create a postgres function: 
        // create or replace function get_server_time() returns timestamptz as $$ select now(); $$ language sql;
        const { data, error } = await supabase.rpc('get_server_time');

        const end = Date.now();
        const rtt = end - start;

        if (error) {
          // Fallback: If RPC is not set up, we just log and default to 0 (assume synced)
          // or we could try to query a table, but that's potentially slow/heavy.
          console.warn('TimeSyncService: get_server_time RPC failed, defaulting to local time.', error);
          this.offset = 0;
        } else {
          const serverTime = new Date(data).getTime();
          // serverTime is approximately (start + rtt/2) in server's clock
          // But 'data' is the time AT the server processing.
          // So ServerTime(at receipt) = data
          // ClientTime(at receipt) approx = start + rtt/2
          // Offset = Server - Client
          const estimatedClientTimeAtServerTick = start + (rtt / 2);
          this.offset = serverTime - estimatedClientTimeAtServerTick;

          console.log(`TimeSyncService: Synced. RTT=${rtt}ms, Offset=${this.offset}ms`);
        }
        this.isSynced = true;
      } catch (err) {
        console.error('TimeSyncService: Sync failed', err);
        this.offset = 0; // Safe fallback
      } finally {
        this.syncPromise = null;
      }
    })();

    return this.syncPromise;
  }

  /**
   * Returns the current estimated server time.
   * @returns {number} Timestamp in ms
   */
  getNow() {
    return Date.now() + this.offset;
  }

  /**
   * Converts a local timestamp to estimated server time.
   * @param {number} localTime 
   * @returns {number}
   */
  toServerTime(localTime) {
    return localTime + this.offset;
  }

  /**
   * Converts a server timestamp to local device time (for display logic that relies on local clock duration).
   * Note: Usually not needed if we calculate durations (serverNow - serverEndsAt).
   */
  toLocalTime(serverTime) {
    return serverTime - this.offset;
  }
}

const timeSyncService = new TimeSyncService();
export default timeSyncService;
